<script setup lang="ts">
import { computed } from 'vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'

// Disabilities that require the Individualized Health Plan (IHP) reminder
// sentence. Strings match the `disability` values returned by the
// /presentlevels/api/v1/impact-of-disability/disabilities endpoint.
const MEDICAL_SENTENCE_DISABILITIES: readonly string[] = [
  'Other Health Impairment',
  'Orthopedic Disability',
  'Traumatic Brain Injury',
  'Autism'
]

interface Props {
  disabilityLabel: string
}

const props = defineProps<Props>()

const showsMedicalSentence = computed(() =>
  MEDICAL_SENTENCE_DISABILITIES.includes(props.disabilityLabel)
)
</script>

<template>
  <GbInlineMessage
    gbTheme="grayLight"
    class="mb-4 pl-7"
    data-testid="iod-guardrail"
    role="note"
  >
    <template #icon></template>
    <p class="italic text-sm leading-6 m-0 text-gray-800">
      Remember: The Impact of Disability statement is the 'Why' behind a student's services.
      It must explicitly link the student's {{ disabilityLabel }} to their specific barriers in the general education curriculum.
      If the Full and Individual Evaluation (FIE) identifies a deficit related to {{ disabilityLabel }},
      your impact statement must describe how that deficit prevents progress.<span
        v-if="showsMedicalSentence"
        data-testid="iod-guardrail-medical-sentence"
      > The selected eligibility {{ disabilityLabel }} <strong>may or may not</strong> require a clinical
        Individualized Health Plan (IHP). Please consult with the School Nurse to ensure medical protocols,
        medication administration, or Emergency Action Plans (EAPs) are attached as a supplement to this IEP,
        if necessary. Supplemental information is attached in your IEP software (e.g. Frontline).</span>
      This message is a reminder only. It doesn't belong in the IEP.
    </p>
  </GbInlineMessage>
</template>
